import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import { Button } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ThemeIconDropdown from '../theme/ThemeIconDropdown';
import { Handshake, HomeIcon } from 'lucide-react';
import { ContactMail, GroupAdd, Quiz, SettingsSuggest } from '@mui/icons-material';
import Logo from '../logo.svg';
import { useNavigate } from 'react-router-dom';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: 'blur(24px)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: '8px 12px',
}));

export default function MenuBar({
  scrollToSection,
  highlightsRef,
  servicesRef,
  whyChooseUsRef,
  faqsRef,
  contactUsRef,
}) {
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const navigate = useNavigate();

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 'calc(var(--template-frame-height, 0px) + 28px)',
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          <img
            src={Logo}
            alt="Logo"
            style={{ height: 30, width: 30, objectFit: 'contain', borderRadius: 8 }}
          />
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={<HomeIcon />}
                onClick={() => scrollToSection(highlightsRef)}
              >
                Home
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={<SettingsSuggest />}
                onClick={() => scrollToSection(servicesRef)}
              >
                Services
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={<Handshake />}
                onClick={() => scrollToSection(whyChooseUsRef)}
              >
                Why Choose Us
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                startIcon={<GroupAdd />}
                onClick={() => navigate('/careers')}
              >
                Careers
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                sx={{ minWidth: 0 }}
                startIcon={<Quiz />}
                onClick={() => scrollToSection(faqsRef)}
              >
                FAQ
              </Button>
              <Button
                variant="text"
                color="info"
                size="small"
                sx={{ minWidth: 0 }}
                startIcon={<ContactMail />}
                onClick={() => scrollToSection(contactUsRef, false)}
              >
                Contact Us
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <ThemeIconDropdown />
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
            <ThemeIconDropdown size="medium" />
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="top"
              open={open}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  top: 'var(--template-frame-height, 0px)',
                },
              }}
            >
              <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                >
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(highlightsRef);
                      setOpen(false);
                    }}
                  >
                    Home
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(servicesRef);
                      setOpen(false);
                    }}
                  >
                    Services
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(whyChooseUsRef);
                      setOpen(false);
                    }}
                  >
                    Why Choose Us
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      navigate('/careers');
                      setOpen(false);
                    }}
                  >
                    Careers
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(faqsRef);
                      setOpen(false);
                    }}
                  >
                    FAQ
                  </Button>
                  <Button
                    variant="text"
                    color="info"
                    onClick={() => {
                      scrollToSection(contactUsRef, false);
                      setOpen(false);
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
